import React from 'react';
import styled from 'styled-components';
import Label from './Label';
import ChipGroup from './ChipGroup';

/**
 * PreferenceGroup Component - Wrapper combining Label + ChipGroup + Error display
 *
 * @param {Object} props
 * @param {string} props.label - Label text for the group
 * @param {Array<{value: string, label: string}>} props.options - Array of chip options
 * @param {string|string[]} props.selectedValues - Selected value(s)
 * @param {boolean} [props.multiSelect=false] - Allow multiple selections
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} [props.error] - Error message to display
 * @param {boolean} [props.required=false] - Whether the field is required
 * @param {boolean} [props.disabled=false] - Whether the group is disabled
 * @param {string} [props.id] - ID for the group (for aria-labelledby)
 * @param {string} [props.helperText] - Helper text displayed between label and chips
 * @param {string} [props.className] - Additional CSS class
 */
const PreferenceGroup = ({
  label,
  options,
  selectedValues,
  multiSelect = false,
  onChange,
  error,
  required = false,
  disabled = false,
  id,
  helperText,
  className,
}) => {
  const labelId = id ? `${id}-label` : undefined;

  return (
    <PreferenceGroupContainer className={className}>
      <Label
        text={label}
        required={required}
        htmlFor={labelId}
      />

      {helperText && <HelperText>{helperText}</HelperText>}

      <ChipGroup
        options={options}
        selectedValues={selectedValues}
        multiSelect={multiSelect}
        onChange={onChange}
        disabled={disabled}
        ariaLabelledBy={labelId}
      />

      {error && (
        <ErrorMessage role="alert">
          {error}
        </ErrorMessage>
      )}
    </PreferenceGroupContainer>
  );
};

const PreferenceGroupContainer = styled.div`
  /* Position */
  position: relative;

  /* Layout */
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HelperText = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 1.4;
  color: #45446b;
  padding-left: 8px;
`;

const ErrorMessage = styled.div`
  /* Typography */
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  color: #e03333;

  /* Layout */
  margin-top: 8px;
  padding-left: 8px;
`;

export default PreferenceGroup;
